const HINT_FADE_MS = 2000

let _hintEl = null
let _toggleBtn = null
let _exitBtn = null
let _modeBtn = null
let _clearBtn = null

export function initUI({ getModelGroup, onExit, onModeToggle, onClearSketches }) {
  _hintEl = document.getElementById('ar-hint')
  _toggleBtn = document.getElementById('toggle-overlay-btn')
  _exitBtn = document.getElementById('exit-ar-btn')
  _modeBtn = document.getElementById('mode-toggle-btn')
  _clearBtn = document.getElementById('clear-sketches-btn')

  _toggleBtn.addEventListener('click', () => {
    // getModelGroup may handle the toggle itself (e.g. ruin mode flips
    // the ruin group instead) and return null. In that case we just
    // show a generic hint; the toggle already happened.
    const group = getModelGroup()
    if (!group) {
      showHint('Toggled', HINT_FADE_MS)
      return
    }
    group.visible = !group.visible
    showHint(group.visible ? 'Overlay shown' : 'Overlay hidden', HINT_FADE_MS)
  })

  _exitBtn.addEventListener('click', () => onExit?.())
  _modeBtn.addEventListener('click', () => onModeToggle?.())
  _clearBtn.addEventListener('click', () => onClearSketches?.())

  // Prevent taps on toolbar buttons from also firing the XR `select`
  // event. Without this, dom-overlay dispatches beforexrselect → if not
  // canceled, select also fires on the XRSession. Result: tapping 👁
  // both toggles the ruin AND moves it (or starts a sketch). Cancel
  // beforexrselect for any tap whose target is a button.
  const overlay = document.getElementById('ar-overlay')
  overlay.addEventListener('beforexrselect', (e) => {
    if (e.target && e.target.closest && e.target.closest('button')) {
      e.preventDefault()
    }
  })
}

// Reflects the active AR mode in the UI: button glyph + clear-button
// visibility + the bottom hint text. Three modes cycle:
//   place 📍 → sketch ✏️ → ruin 🏛️ → place
export function setMode(mode) {
  if (!_modeBtn) return
  if (mode === 'sketch') {
    _modeBtn.textContent = '✏️'
    _modeBtn.title = 'Sketch mode — tap mode button to advance'
    if (_clearBtn) _clearBtn.hidden = false
    showHint('Tap to start a line • tap again to end it', null)
  } else if (mode === 'ruin') {
    _modeBtn.textContent = '🏛️'
    _modeBtn.title = 'Ruin mode — tap mode button to advance'
    if (_clearBtn) _clearBtn.hidden = false
    showHint('Tap the floor at the wall corner', null)
  } else {
    _modeBtn.textContent = '📍'
    _modeBtn.title = 'Place mode — tap mode button to advance'
    if (_clearBtn) _clearBtn.hidden = true
    showHint('Tap to place • mode button cycles tools', null)
  }
}

// Replaces the hint with the current ruin-state guidance. Called by
// the AR controller after each tap in ruin mode.
export function setRuinHint(text) {
  if (!text) return
  showHint(text, null)
}

export function showAROverlay() {
  document.getElementById('ar-overlay').hidden = false
  document.getElementById('start-screen').style.display = 'none'
  showHint('Point at the floor and tap to place', null)
}

export function hideAROverlay() {
  document.getElementById('ar-overlay').hidden = true
  document.getElementById('start-screen').style.display = ''
}

export function fadeHintAfterPlacement() {
  if (!_hintEl) return
  setTimeout(() => _hintEl.classList.add('fade'), HINT_FADE_MS)
}

export function showHint(text, autoFadeMs = HINT_FADE_MS) {
  if (!_hintEl) return
  _hintEl.textContent = text
  _hintEl.classList.remove('fade')
  if (autoFadeMs) {
    setTimeout(() => _hintEl.classList.add('fade'), autoFadeMs)
  }
}

// Maps the AR controller's hit-test state to user-visible guidance.
export function setHitState(state) {
  switch (state) {
    case 'searching':
      showHint('Move slowly to scan the floor…', null)
      break
    case 'tracking':
      showHint('Tap to place', null)
      break
    case 'placed':
      // Caller usually fades after placement; do nothing here.
      break
  }
}

// Persistent hint after the first placement. Subsequent taps move the
// ruin to the new reticle position, so the message stays put as a
// reminder of available controls.
export function showPlacedHint() {
  showHint('Tap to move • 👁 toggle • ✕ exit', null)
}

export function setSupportHint(text, isError = false) {
  const el = document.getElementById('support-hint')
  if (!el) return
  el.textContent = text
  el.classList.toggle('error', isError)
}

export function setPlaneStatus(counts, hasCorner = false) {
  const el = document.getElementById('plane-status')
  if (!el) return
  if (!counts || counts.total === 0) {
    el.textContent = 'scanning…'
    return
  }
  const bits = []
  bits.push(`${counts.horizontal} floor${counts.horizontal === 1 ? '' : 's'}`)
  bits.push(`${counts.vertical} wall${counts.vertical === 1 ? '' : 's'}`)
  if (counts.other) bits.push(`${counts.other} ?`)
  if (hasCorner) bits.push('✨ corner')
  el.textContent = '📐 ' + bits.join(' • ')
}

export function setPlaneStatusVisible(visible) {
  const el = document.getElementById('plane-status')
  if (el) el.hidden = !visible
}
